import { type JSX, type ReactNode, useCallback, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface AccordionProps {
  /** Title shown in the clickable header */
  title: string;
  /** Content revealed when expanded */
  children: ReactNode;
  /** Start in expanded state */
  defaultOpen?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Accordion - Collapsible content section with smooth animation.
 * Follows the spec: "Chevron icon rotates 90 degrees on expand,
 * smooth height transition (150ms ease-out)"
 */
export function Accordion({
  children,
  className,
  defaultOpen = false,
  title,
}: AccordionProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  return (
    <div className={clsx('border border-default rounded-lg overflow-hidden my-4', className)}>
      {/* Clickable header */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center justify-between w-full px-4 py-3 bg-primary/70 hover:bg-primary/80 transition-colors text-left rounded-t-lg"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-primary-foreground">{title}</span>
        <ChevronRightIcon
          className={clsx(
            'h-5 w-5 text-primary-foreground/70 transition-transform duration-150 ease-out',
            {
              'rotate-90': isOpen,
            },
          )}
        />
      </button>

      {/* Collapsible content with grid-row animation (no max-height cap) */}
      <div
        className={clsx('grid transition-[grid-template-rows] duration-150 ease-out', {
          'grid-rows-[0fr]': !isOpen,
          'grid-rows-[1fr]': isOpen,
        })}
      >
        <div className="overflow-hidden">
          <div className="px-4 py-3 border-t border-default bg-surface">
            <div className="text-sm text-muted leading-relaxed [&_strong]:text-foreground">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
